import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { exportAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Download,
    Users,
    ClipboardList
} from 'lucide-react';

export const AdminExport = () => {
    const navigate = useNavigate();

    const handleExportCustomers = async () => {
        try {
            const response = await exportAPI.customers();
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'customers.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Customers exported!');
        } catch (error) {
            toast.error('Failed to export customers');
        }
    };

    const handleExportJobs = async () => {
        try {
            const response = await exportAPI.jobs();
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'jobs.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Jobs exported!');
        } catch (error) {
            toast.error('Failed to export jobs');
        }
    };

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/settings')}
                        className="h-10 w-10"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                            Data Export
                        </h1>
                        <p className="text-slate-500 text-sm">Export your data as CSV</p>
                    </div>
                </div>

                {/* Export Options */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight">
                            Export Data
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <Button
                            variant="outline"
                            className="w-full h-16 justify-start"
                            onClick={handleExportCustomers}
                            data-testid="export-customers-btn"
                        >
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                                <Users className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="text-left">
                                <p className="font-semibold">Export Customers</p>
                                <p className="text-sm text-slate-500">Download all customers as CSV</p>
                            </div>
                            <Download className="h-5 w-5 ml-auto text-slate-400" />
                        </Button>
                        
                        <Button
                            variant="outline"
                            className="w-full h-16 justify-start"
                            onClick={handleExportJobs}
                            data-testid="export-jobs-btn"
                        >
                            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mr-4">
                                <ClipboardList className="h-6 w-6 text-amber-600" />
                            </div>
                            <div className="text-left">
                                <p className="font-semibold">Export Jobs</p>
                                <p className="text-sm text-slate-500">Download all jobs as CSV</p>
                            </div>
                            <Download className="h-5 w-5 ml-auto text-slate-400" />
                        </Button>
                    </CardContent>
                </Card>

                <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4">
                        <p className="text-sm text-amber-800">
                            <strong>Note:</strong> Exported CSV files can be opened in Excel, Google Sheets, 
                            or any spreadsheet application for further analysis.
                        </p>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};
